// src/Screens/Feedback.jsx
// Players' feedback board, inside the app shell. Deliberately neutral (stone
// greys, ink as the accent, coloured dots only for status) so it reads as a
// standalone product board and can be lifted into other apps.
// - Ideas: one compact list with search and sort; tap a row to open it.
//   Shipped ideas fold away at the bottom; bug reports are only listed to
//   whoever sent them.
// - Updates: the changelog posted from the admin dashboard, then the older
//   updates from src/content/pastUpdates.js. Settings' What's New opens it.
import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import { ChevronDown, ChevronUp, Loader2, Check, Plus, Search, MessageSquare, X } from "lucide-react";
import { auth } from "../firebase";
import mobileAuthService from "../Services/mobileAuthService";
import feedbackService, { CATEGORIES, CATEGORY_LABELS, STATUS_LABELS, isPublicIdea, voteCountOf } from "../Services/feedbackService";
import BottomSheet from "../Components/BottomSheet";
import pastUpdates from "../content/pastUpdates";

// Last update a player has seen, so Settings can show a dot for new ones.
export const UPDATES_SEEN_KEY = "lastViewedUpdate";

const TEAM_NAME = "the NameIt! team";

const STATUS_DOT = {
  open: "bg-stone-400",
  planned: "bg-sky-500",
  in_progress: "bg-amber-500",
  done: "bg-emerald-500",
};

const TYPE_TAGS = { feature: "Feature", improvement: "Improvement", bug: "Bug" };

// Wording in the post sheet for each type.
const COPY = {
  feature: {
    note: "Other people can vote for your request.",
    titlePlaceholder: "e.g. Add a Food category",
    descriptionPlaceholder: "Describe your request. What would you like, and how would it help?",
    submit: "Submit request",
  },
  improvement: {
    note: "Other people can vote for your request.",
    titlePlaceholder: "e.g. Make the round timer easier to see",
    descriptionPlaceholder: "Describe what could work better, and how you'd change it.",
    submit: "Submit request",
  },
  bug: {
    note: `Bug reports go straight to ${TEAM_NAME}.`,
    titlePlaceholder: "e.g. Game froze after round 3",
    descriptionPlaceholder: "Describe what happened. What were you doing, what did you expect, and which device were you on?",
    submit: "Submit bug report",
  },
};

const toDate = (v) => (v?.toDate ? v.toDate() : v ? new Date(v) : null);

const shortDate = (date) =>
  date
    ? date.toLocaleDateString(undefined, {
        day: "numeric",
        month: "short",
        ...(date.getFullYear() !== new Date().getFullYear() ? { year: "numeric" } : {}),
      })
    : "";

const longDate = (date) => date.toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" });

// Words worth matching on, for search and for spotting an idea that's already posted.
const STOP_WORDS = new Set(["the", "a", "an", "to", "and", "or", "of", "for", "in", "on", "with", "please", "add", "can", "you", "i", "we", "it", "is", "be", "my", "me"]);
const keywords = (text) =>
  (text || "").toLowerCase().split(/[^a-z0-9]+/).filter((w) => w.length > 2 && !STOP_WORDS.has(w));

function VotePill({ post, uid, onVote }) {
  const [voting, setVoting] = useState(false);
  const hasVoted = !!uid && post.votes?.includes(uid);
  const handle = async (e) => {
    e.stopPropagation();
    if (voting) return;
    setVoting(true);
    try {
      await onVote(post);
    } finally {
      setVoting(false);
    }
  };
  return (
    <button
      type="button"
      onClick={handle}
      disabled={voting}
      aria-pressed={hasVoted}
      aria-label={`${hasVoted ? "Remove your vote" : "Vote"} (${voteCountOf(post)} votes)`}
      className={`flex flex-col items-center justify-center w-11 h-12 rounded-lg border text-[13px] font-semibold tabular-nums transition-colors flex-shrink-0 ${
        hasVoted
          ? "bg-stone-900 border-stone-900 text-white"
          : "bg-white border-stone-200 text-stone-700 hover:border-stone-400"
      }`}
    >
      {voting ? <Loader2 size={14} className="animate-spin" /> : <ChevronUp size={16} strokeWidth={2.5} />}
      <span className="leading-none mt-0.5">{voteCountOf(post)}</span>
    </button>
  );
}

function Meta({ post }) {
  const date = toDate(post.createdAt);
  return (
    <p className="flex items-center flex-wrap gap-x-1.5 gap-y-0.5 text-xs text-stone-500 mt-1.5">
      <span className="inline-flex items-center gap-1.5 font-medium text-stone-700">
        <span className={`w-1.5 h-1.5 rounded-full ${STATUS_DOT[post.status] || STATUS_DOT.open}`} aria-hidden="true" />
        {STATUS_LABELS[post.status] || STATUS_LABELS.open}
      </span>
      <span aria-hidden="true">·</span>
      <span>{TYPE_TAGS[post.category] || TYPE_TAGS.feature}</span>
      {date && !post.migrated && (
        <>
          <span aria-hidden="true">·</span>
          <span>{shortDate(date)}</span>
        </>
      )}
      {post.teamReply?.text && (
        <>
          <span aria-hidden="true">·</span>
          <span className="inline-flex items-center gap-1">
            <MessageSquare size={11} aria-hidden="true" />
            Reply
          </span>
        </>
      )}
    </p>
  );
}

// One row of the list. Tapping it opens the full text and any team reply.
function IdeaRow({ post, uid, onVote }) {
  const [open, setOpen] = useState(false);
  const isBug = post.category === "bug";
  return (
    <li>
      <div
        role="button"
        tabIndex={0}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setOpen((v) => !v);
          }
        }}
        className="flex items-start gap-3 px-4 py-3.5 cursor-pointer hover:bg-stone-50 focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-stone-900"
      >
        <div className="flex-1 min-w-0">
          <h3 className="text-[15px] font-semibold text-stone-900 leading-snug">{post.title}</h3>
          {post.description && (
            <p className={`text-sm text-stone-500 mt-0.5 leading-relaxed whitespace-pre-line ${open ? "" : "line-clamp-2"}`}>
              {post.description}
            </p>
          )}
          <Meta post={post} />
          {open && post.teamReply?.text && (
            <div className="mt-3 rounded-lg bg-stone-100 px-3 py-2.5">
              <p className="text-xs font-semibold text-stone-900">Reply from {TEAM_NAME}</p>
              <p className="text-sm text-stone-700 mt-0.5 whitespace-pre-line">{post.teamReply.text}</p>
            </div>
          )}
        </div>
        {!isBug && <VotePill post={post} uid={uid} onVote={onVote} />}
      </div>
    </li>
  );
}

function List({ children }) {
  return <ul className="bg-white rounded-xl border border-stone-200 divide-y divide-stone-100 overflow-hidden">{children}</ul>;
}

function SectionLabel({ children, right }) {
  return (
    <div className="flex items-center justify-between mb-2 px-1">
      <h2 className="text-xs font-semibold text-stone-500">{children}</h2>
      {right}
    </div>
  );
}

function SkeletonList() {
  return (
    <List>
      {[0, 1, 2, 3].map((i) => (
        <li key={i} className="flex gap-3 px-4 py-4 animate-pulse">
          <div className="flex-1 space-y-2">
            <div className="h-3.5 bg-stone-200 rounded w-2/3" />
            <div className="h-3 bg-stone-100 rounded w-full" />
            <div className="h-3 bg-stone-100 rounded w-1/3" />
          </div>
          <div className="w-11 h-12 bg-stone-100 rounded-lg" />
        </li>
      ))}
    </List>
  );
}

// Title, description, then type (feature by default; the admin can change it).
// While an idea's title is typed, ideas that look the same show up so people
// can vote instead of posting again.
function PostSheet({ isOpen, onClose, posts, uid, onVote, onSubmit }) {
  const [kind, setKind] = useState("feature");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const isBug = kind === "bug";

  useEffect(() => {
    if (!isOpen) return;
    setKind("feature");
    setTitle("");
    setDescription("");
    setError("");
  }, [isOpen]);

  const similar = useMemo(() => {
    if (isBug) return [];
    const words = keywords(title);
    if (!words.length) return [];
    return posts
      .filter(isPublicIdea)
      .map((p) => {
        const text = keywords(`${p.title} ${p.description}`);
        const hits = words.filter((w) => text.some((t) => t.startsWith(w) || w.startsWith(t))).length;
        return { p, hits };
      })
      .filter((x) => x.hits > 0)
      .sort((a, b) => b.hits - a.hits || voteCountOf(b.p) - voteCountOf(a.p))
      .slice(0, 3)
      .map((x) => x.p);
  }, [title, posts, isBug]);

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim() || !description.trim()) {
      setError("Add a title and a description.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      await onSubmit({ title, description, category: kind });
    } catch (err) {
      console.error("Failed to submit feedback:", err);
      setError("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const copy = COPY[kind];
  const fieldClass =
    "w-full border border-stone-300 rounded-lg px-3.5 py-2.5 text-[15px] text-stone-900 bg-white focus:ring-2 focus:ring-stone-900/10 focus:border-stone-900 outline-none placeholder:text-stone-400";
  const labelClass = "block text-sm font-medium text-stone-900 mb-1.5";

  return (
    <BottomSheet isOpen={isOpen} onClose={onClose} title="New post" subtitle={copy.note}>
      <form onSubmit={submit} className="space-y-4">
        <div>
          <label htmlFor="feedback-title" className={labelClass}>Title</label>
          <input
            id="feedback-title"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={copy.titlePlaceholder}
            maxLength={100}
            className={fieldClass}
          />
        </div>

        {similar.length > 0 && (
          <div className="rounded-lg border border-stone-200 bg-stone-50">
            <p className="text-xs font-medium text-stone-600 px-3 pt-2.5 pb-1">Similar posts. Vote for one instead?</p>
            <ul className="divide-y divide-stone-200">
              {similar.map((p) => (
                <li key={p.id} className="flex items-center gap-3 px-3 py-2">
                  <span className="flex-1 min-w-0 text-sm font-medium text-stone-900 line-clamp-2">{p.title}</span>
                  <VotePill post={p} uid={uid} onVote={onVote} />
                </li>
              ))}
            </ul>
          </div>
        )}

        <div>
          <label htmlFor="feedback-description" className={labelClass}>Description</label>
          <textarea
            id="feedback-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={copy.descriptionPlaceholder}
            rows={4}
            maxLength={1000}
            className={`${fieldClass} resize-none`}
          />
        </div>

        <div>
          <label htmlFor="feedback-type" className={labelClass}>Type</label>
          <div className="relative">
            <select
              id="feedback-type"
              value={kind}
              onChange={(e) => { setKind(e.target.value); setError(""); }}
              className={`${fieldClass} appearance-none pr-10`}
            >
              {CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {CATEGORY_LABELS[c]}
                </option>
              ))}
            </select>
            <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-500 pointer-events-none" aria-hidden="true" />
          </div>
        </div>

        {error && <p className="text-sm text-red-700">{error}</p>}

        <button
          type="submit"
          disabled={submitting}
          className="w-full h-12 rounded-lg font-semibold text-white bg-stone-900 hover:bg-stone-800 disabled:bg-stone-400 transition-colors"
        >
          {submitting ? "Sending…" : copy.submit}
        </button>
      </form>
    </BottomSheet>
  );
}

export default function Feedback() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get("tab") === "updates" ? "updates" : "ideas";
  const setTab = (t) => setSearchParams(t === "updates" ? { tab: "updates" } : {}, { replace: true });

  const [sort, setSort] = useState("top");
  const [query, setQuery] = useState("");
  const [posts, setPosts] = useState([]);
  const [changelog, setChangelog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [showSheet, setShowSheet] = useState(false);
  const [thanks, setThanks] = useState("");
  const [showShipped, setShowShipped] = useState(true);
  const [uid, setUid] = useState(null);

  // Read once, before this visit marks everything seen.
  const [seenBefore] = useState(() => {
    try {
      return localStorage.getItem(UPDATES_SEEN_KEY) || "";
    } catch {
      return "";
    }
  });

  useEffect(() => {
    // Same silent sign-in handoff the Account page uses — a player arriving from the
    // mobile app lands here already identified, anon or signed-in, no second account.
    mobileAuthService.handleTokenFromUrl().catch(console.error);
    const unsubscribe = onAuthStateChanged(auth, (user) => setUid(user?.uid || null));
    loadData();
    return () => unsubscribe();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setLoadError("");
    try {
      const [postsData, changelogData] = await Promise.all([feedbackService.listPosts(), feedbackService.listChangelog()]);
      setPosts(postsData);
      setChangelog(changelogData);
    } catch (err) {
      console.error("Failed to load feedback:", err);
      setLoadError("Couldn't load the board. Check your connection and try again.");
    } finally {
      setLoading(false);
    }
  };

  const updates = useMemo(() => {
    const live = changelog.map((e) => ({ id: e.id, date: toDate(e.date), title: e.title, description: e.description, postId: e.postId }));
    const past = pastUpdates.map((u) => ({ id: u.date, date: new Date(u.date), title: u.title, items: u.items }));
    return [...live, ...past].filter((u) => u.date && !Number.isNaN(u.date.getTime())).sort((a, b) => b.date - a.date);
  }, [changelog]);

  // Every update has now been seen; the newest date is what Settings compares
  // against for its dot.
  useEffect(() => {
    if (tab !== "updates" || !updates.length) return;
    try {
      localStorage.setItem(UPDATES_SEEN_KEY, updates[0].date.toISOString().slice(0, 10));
    } catch {
      /* storage unavailable */
    }
  }, [tab, updates]);

  const handleVote = async (post) => {
    const nowVoted = await feedbackService.toggleVote(post.id, post.votes || []);
    const me = uid || auth.currentUser?.uid;
    setPosts((prev) =>
      prev.map((p) =>
        p.id === post.id
          ? {
              ...p,
              votes: nowVoted ? [...(p.votes || []), me] : (p.votes || []).filter((v) => v !== me),
              voteCount: voteCountOf(p) + (nowVoted ? 1 : -1),
            }
          : p
      )
    );
  };

  const handleCreatePost = async (data) => {
    const newPost = await feedbackService.createPost(data);
    setPosts((prev) => [{ ...newPost, createdAt: new Date() }, ...prev]);
    setShowSheet(false);
    setThanks(
      data.category === "bug"
        ? `Thanks! Your report went to ${TEAM_NAME}. It's listed under Your bug reports.`
        : "Thanks! Your post is up. Share it so others can vote."
    );
  };

  const visible = useMemo(() => {
    const words = keywords(query);
    const matches = (p) => {
      if (!words.length) return true;
      const text = `${p.title} ${p.description}`.toLowerCase();
      return words.every((w) => text.includes(w));
    };
    const by = sort === "new"
      ? (a, b) => (toDate(b.createdAt)?.getTime() || 0) - (toDate(a.createdAt)?.getTime() || 0)
      : (a, b) => voteCountOf(b) - voteCountOf(a);
    return [...posts].filter(matches).sort(by);
  }, [posts, sort, query]);

  const activeIdeas = visible.filter((p) => isPublicIdea(p) && p.status !== "done");
  const shippedIdeas = visible.filter((p) => isPublicIdea(p) && p.status === "done");
  const myBugs = visible.filter((p) => p.category === "bug" && uid && p.authorId === uid && !p.mergedInto);

  const openSheet = () => {
    setThanks("");
    setShowSheet(true);
  };

  return (
    <div className="bg-stone-50 min-h-[calc(100vh-4rem)] -mb-24 sm:-mb-8">
      <div className="mx-auto max-w-2xl px-4 pt-5 pb-28">
        <div className="flex items-center justify-between gap-3">
          <h1 className="text-2xl font-semibold tracking-tight text-stone-900">Feedback</h1>
          {tab === "ideas" && (
            <button
              type="button"
              onClick={openSheet}
              className="hidden sm:inline-flex items-center gap-1.5 h-9 px-3.5 rounded-lg bg-stone-900 hover:bg-stone-800 text-white text-sm font-medium"
            >
              <Plus size={16} aria-hidden="true" />
              New post
            </button>
          )}
        </div>

        <nav className="flex gap-6 border-b border-stone-200 mt-3 mb-4" aria-label="Sections">
          {[
            { key: "ideas", label: "Ideas" },
            { key: "updates", label: "Updates" },
          ].map(({ key, label }) => (
            <button
              key={key}
              type="button"
              aria-current={tab === key ? "page" : undefined}
              onClick={() => setTab(key)}
              className={`-mb-px py-2.5 text-sm font-medium border-b-2 transition-colors ${
                tab === key ? "border-stone-900 text-stone-900" : "border-transparent text-stone-500 hover:text-stone-800"
              }`}
            >
              {label}
            </button>
          ))}
        </nav>

        {loading ? (
          <SkeletonList />
        ) : loadError ? (
          <div className="text-center py-12">
            <p className="text-sm text-stone-500 mb-3">{loadError}</p>
            <button type="button" onClick={loadData} className="h-10 px-4 rounded-lg border border-stone-300 bg-white text-sm font-medium text-stone-900">
              Try again
            </button>
          </div>
        ) : tab === "ideas" ? (
          <>
            {/* Search and sort on one line */}
            <div className="flex gap-2 mb-4">
              <label className="relative flex-1 min-w-0">
                <span className="sr-only">Search ideas</span>
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 pointer-events-none" aria-hidden="true" />
                <input
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search ideas"
                  className="w-full h-10 pl-9 pr-8 rounded-lg border border-stone-200 bg-white text-sm text-stone-900 placeholder:text-stone-400 focus:ring-2 focus:ring-stone-900/10 focus:border-stone-900 outline-none"
                />
                {query && (
                  <button
                    type="button"
                    onClick={() => setQuery("")}
                    aria-label="Clear search"
                    className="absolute right-1.5 top-1/2 -translate-y-1/2 w-7 h-7 flex items-center justify-center rounded text-stone-400 hover:text-stone-700"
                  >
                    <X size={14} />
                  </button>
                )}
              </label>
              <label className="relative flex-shrink-0">
                <span className="sr-only">Sort</span>
                <select
                  value={sort}
                  onChange={(e) => setSort(e.target.value)}
                  className="appearance-none h-10 pl-3 pr-8 rounded-lg border border-stone-200 bg-white text-sm font-medium text-stone-700 focus:ring-2 focus:ring-stone-900/10 focus:border-stone-900 outline-none"
                >
                  <option value="top">Top</option>
                  <option value="new">New</option>
                </select>
                <ChevronDown size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-stone-500 pointer-events-none" aria-hidden="true" />
              </label>
            </div>

            {thanks && (
              <p role="status" className="flex items-start gap-2 text-sm text-stone-800 bg-white border border-stone-200 rounded-lg px-3 py-2.5 mb-4">
                <Check size={16} className="flex-shrink-0 mt-0.5 text-emerald-600" aria-hidden="true" />
                {thanks}
              </p>
            )}

            {activeIdeas.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-xl border border-stone-200">
                <p className="text-sm font-medium text-stone-900">{query ? "No ideas match that." : "No ideas yet."}</p>
                <p className="text-sm text-stone-500 mt-1">{query ? "Try other words, or post it yourself." : "Be the first to suggest one."}</p>
              </div>
            ) : (
              <List>
                {activeIdeas.map((post) => <IdeaRow key={post.id} post={post} uid={uid} onVote={handleVote} />)}
              </List>
            )}

            {myBugs.length > 0 && (
              <section className="mt-6">
                <SectionLabel>Your bug reports</SectionLabel>
                <List>
                  {myBugs.map((post) => <IdeaRow key={post.id} post={post} uid={uid} onVote={handleVote} />)}
                </List>
              </section>
            )}

            {shippedIdeas.length > 0 && (
              <section className="mt-6">
                <button
                  type="button"
                  onClick={() => setShowShipped((v) => !v)}
                  aria-expanded={showShipped}
                  className="w-full flex items-center justify-between min-h-[40px] px-1 text-left"
                >
                  <span className="text-xs font-semibold text-stone-500">Shipped · {shippedIdeas.length}</span>
                  {showShipped ? <ChevronUp size={16} className="text-stone-500" /> : <ChevronDown size={16} className="text-stone-500" />}
                </button>
                {showShipped && (
                  <List>
                    {shippedIdeas.map((post) => <IdeaRow key={post.id} post={post} uid={uid} onVote={handleVote} />)}
                  </List>
                )}
              </section>
            )}
          </>
        ) : (
          <List>
            {updates.map((u) => {
              const shipped = u.postId && posts.find((p) => p.id === u.postId);
              const isNew = !!seenBefore && u.date.toISOString().slice(0, 10) > seenBefore;
              return (
                <li key={u.id} className="px-4 py-4">
                  <div className="flex items-center gap-2">
                    <time className="text-xs text-stone-500">{longDate(u.date)}</time>
                    {isNew && <span className="text-[10px] font-semibold uppercase tracking-wide text-white bg-stone-900 rounded px-1.5 py-0.5">New</span>}
                  </div>
                  <h3 className="text-[15px] font-semibold text-stone-900 mt-1">{u.title}</h3>
                  {u.items ? (
                    <ul className="mt-1.5 space-y-1 text-sm text-stone-600 leading-relaxed">
                      {u.items.map(([name, text]) => (
                        <li key={name}>
                          <span className="font-medium text-stone-900">{name}.</span> {text}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="mt-1 text-sm text-stone-600 leading-relaxed whitespace-pre-line">{u.description}</p>
                  )}
                  {shipped && (
                    <p className="mt-2.5 inline-flex items-center gap-1.5 text-xs font-medium text-stone-700 bg-stone-100 rounded-md px-2 py-1">
                      <Check size={12} className="text-emerald-600" aria-hidden="true" />
                      Requested: {shipped.title} · {voteCountOf(shipped)} votes
                    </p>
                  )}
                </li>
              );
            })}
          </List>
        )}
      </div>

      {/* Phones get a round button above the tab bar; wider screens use the
          New post button in the header. */}
      {tab === "ideas" && !loading && !loadError && (
        <button
          type="button"
          onClick={openSheet}
          aria-label="New post"
          className="sm:hidden fixed right-4 bottom-24 z-30 w-14 h-14 flex items-center justify-center rounded-full bg-stone-900 hover:bg-stone-800 text-white shadow-lg shadow-stone-900/20"
        >
          <Plus size={24} aria-hidden="true" />
        </button>
      )}

      <PostSheet
        isOpen={showSheet}
        onClose={() => setShowSheet(false)}
        posts={posts}
        uid={uid}
        onVote={handleVote}
        onSubmit={handleCreatePost}
      />
    </div>
  );
}
